import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DataTable, { Column, DataTableRef, DataTableSelectedData } from '@repo/ui/data-table'
import { DeleteIcon, PenIcon, PlusIcon, SavingsIcon, UploadIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { printFormat } from 'iban-ts'
import { useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { currencyEnumMapping } from '../../../../lib/types/enums/common/currency.enum'
import { OlBankAccountInput, OlInput } from '../../../../lib/types/models/ol/ol-input'
import { IdInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import OlBankAccountForm from './ol-bank-account-form'

const OlBankAccountsOlForm = () => {
  const form = useFormContext<OlInput>()
  const bankAccounts = form.watch('bankAccounts') || []
  const [selectedRows, setSelectedRows] = useState<
    DataTableSelectedData<IdInput<OlBankAccountInput>>
  >({
    isAllSelected: false,
    rows: []
  })
  const ref = useRef<DataTableRef>(null)

  const { modalState, selectedData, closeModel, toggleModal } = useModalManager<
    'create' | 'edit' | 'delete',
    OlBankAccountInput
  >(['create', 'edit', 'delete'])

  const columns: Column<IdInput<OlBankAccountInput>>[] = [
    {
      enableSorting: true,
      header: 'Titulaire du compte',
      accessorKey: 'bankAccountHolder'
    },
    {
      enableSorting: true,
      header: 'Pays de séjour',
      accessorKey: 'hostCountries',
      cell: ({ row }) => (
        <div className="flex gap-2">
          {row.original.hostCountries.map((country) => (
            <Badge key={country.id} variant="warning" text={country.name} />
          ))}
        </div>
      ),
      render: (row) => row.hostCountries.map((country) => country.name).join(', ')
    },
    {
      enableSorting: true,
      header: 'Devise',
      accessorKey: 'currency',
      cell: ({ row }) => currencyEnumMapping[row.original.currency].label,
      sortingFn: (a, b) =>
        currencyEnumMapping[a.original.currency].label.localeCompare(
          currencyEnumMapping[b.original.currency].label
        )
    },
    {
      enableSorting: true,
      header: 'IBAN',
      accessorKey: 'iban',
      cell: ({ row }) => printFormat(row.original.iban)
    },
    {
      enableSorting: true,
      header: 'BIC',
      accessorKey: 'bic'
    },
    {
      enableSorting: false,
      header: '',
      exportable: false,
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className={'flex gap-2'}>
          <ActionButton
            variant={'grey'}
            icon={PenIcon}
            onClick={() => toggleModal('edit', row.original)}
          />

          <ActionButton
            variant={'danger'}
            icon={DeleteIcon}
            onClick={() => toggleModal('delete', row.original)}
          />
        </div>
      )
    }
  ]

  return (
    <ActionTableWrapper
      title={'Compte bancaires'}
      actions={[
        <Button
          key={'create'}
          type="button"
          icon={PlusIcon}
          label={'Ajouter un compte bancaire'}
          variant={'outline'}
          onClick={() => toggleModal('create')}
        />,
        <Button
          key={'export'}
          type="button"
          icon={UploadIcon}
          label={'Export CSV IBAN'}
          variant={'ghost'}
          disabled={!selectedRows.isAllSelected && selectedRows.rows.length === 0}
          onClick={() => {
            ref.current?.exportToCsv?.()
          }}
        />
      ]}
    >
      <DataTable<IdInput<OlBankAccountInput>>
        ref={ref}
        display={bankAccounts.length > 0}
        manualSorting={false}
        csvOptions={{
          fileName: `iban-${new Date().toISOString()}`,
          selectedRows: true
        }}
        columns={columns}
        data={bankAccounts.map((bankAccount, index) => ({ ...bankAccount, id: index }))}
        rowCount={bankAccounts.length}
        paginated={false}
        selectable
        selectedData={selectedRows}
        onSelectedDataChange={setSelectedRows}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un compte bancaire'}
        icon={SavingsIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('create')}
      >
        <OlBankAccountForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => toggleModal('create'),
            onConfirm: (data) => {
              form.setValue('bankAccounts', [...bankAccounts, data])
              toggleModal('create')
            }
          })}
        />
      </Modal>

      {selectedData && (
        <>
          <Modal
            isOpen={modalState.edit}
            title={'Modifier un compte bancaire'}
            icon={SavingsIcon}
            size="medium"
            hideActions
            onCancel={() => closeModel('edit')}
          >
            <OlBankAccountForm
              className={MODAL_BODY_CLASSNAME}
              defaultValues={selectedData}
              buttonsConfig={modalButtonConfig({
                onCancel: () => toggleModal('edit'),
                onConfirm: (data) => {
                  form.setValue(
                    'bankAccounts',
                    bankAccounts.map((bankAccount, index) =>
                      index === selectedData.id ? data : bankAccount
                    )
                  )
                  toggleModal('edit')
                }
              })}
            />
          </Modal>

          <Modal
            actionButtonsOptions={{
              confirmText: 'Supprimer',
              cancelText: 'Fermer',
              onConfirm: () => {
                // remove the selected bank account from the bankAccounts array
                form.setValue(
                  'bankAccounts',
                  bankAccounts.filter((_, index) => index !== selectedData.id)
                )
                toggleModal('delete')
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={`Supprimer le compte bancaire de "${selectedData.bankAccountHolder}"?`}
            icon={DeleteIcon}
            variant={'destructive'}
          />
        </>
      )}
    </ActionTableWrapper>
  )
}

export default OlBankAccountsOlForm
